import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Anchor } from 'components/base';

import { ideaPrice } from '../../lib/landingPrice';
import PdpIcon from '../product/pdpIcon';
import { Picture, PriceTag, Responsive, imageShape } from '../landing/shared';

// Gift idea card: one product or a pairing priced together (lib/landingPrice.js ideaPrice).
// The link to the first product is stretched over the whole card, its text is that product's
// name so the product page gets a descriptive anchor.
const GiftIdea = ({ idea, onClick }) => (
  <article id={idea.id} className={classNames('gft-idea', { 'gft-idea--large': idea.large })}>
    <div className="gft-idea-photo">
      <Picture
        image={idea.image}
        sizes={
          idea.large
            ? '(min-width: 992px) 740px, calc(100vw - 40px)'
            : '(min-width: 992px) 420px, calc(100vw - 40px)'
        }
      />
      <span className="gft-chip">
        <PdpIcon name="gift" size={13} />
        {idea.recipient}
      </span>
      {idea.price?.badge && <span className="gft-badge">{idea.price.badge}</span>}
    </div>
    <div className="gft-idea-copy">
      <h3 className="gft-idea-title">{idea.title}</h3>
      <p className="gft-idea-body">
        <Responsive desktop={idea.body} phone={idea.short} />
      </p>
      <p className="gft-idea-footer">
        <span className="gft-idea-products">
          <PdpIcon name="package" size={14} className="gft-phone-icon" />
          <span>
            {/* Pseudo element stretches the link over the whole idea. */}
            <Anchor href={idea.href} className="gft-idea-link" onClick={onClick}>
              {idea.productNames[0]}
            </Anchor>
            {idea.productNames.slice(1).map((name) => ` + ${name}`)}
          </span>
        </span>
        <PriceTag price={idea.price} className="gft-idea-price" />
        <span className="lnd-arrow-link gft-idea-cta" aria-hidden="true">
          Make this gift
          <PdpIcon name="arrowRight" size={14} />
        </span>
      </p>
      {/* Phone CTA, visual only: the product link handles the tap and screen readers get the
          price from the footer, which phones hide visually only. */}
      <span className="gft-idea-button" aria-hidden="true">
        <span>Make this gift</span>
        <span className="gft-idea-button-price">
          {idea.price?.originalPrice ? (
            <>
              <s>{idea.price.originalPrice}</s> <strong>{idea.price.price}</strong>
            </>
          ) : (
            idea.price && `${idea.price.priceFrom ? 'from ' : ''}${idea.price.price}`
          )}
          <PdpIcon name="arrowRight" size={16} />
        </span>
      </span>
    </div>
  </article>
);

// Adds the link (first product) and the price to idea constants that list `products` ids.
export const resolveIdeas = (ideas, dataContext, previewSummary) =>
  ideas.map((idea) => {
    const products = idea.products.map((id) => dataContext.getProduct(id));
    return {
      ...idea,
      href: products[0] ? products[0].url : '/shop',
      price: ideaPrice(products, previewSummary),
    };
  });

GiftIdea.propTypes = {
  idea: PropTypes.shape({
    id: PropTypes.string.isRequired,
    large: PropTypes.bool,
    recipient: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    short: PropTypes.string,
    productNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    image: imageShape.isRequired,
    href: PropTypes.string.isRequired,
    price: PropTypes.shape({
      price: PropTypes.string,
      originalPrice: PropTypes.string,
      priceFrom: PropTypes.bool,
      badge: PropTypes.string,
    }),
  }).isRequired,
  onClick: PropTypes.func,
};

export default GiftIdea;
